import { onBeforeUnmount, ref, watch } from "vue";
import type { Ref } from "vue";
import { useIncidentHub } from "@/composables/useIncidentHub";
import { MediaService } from "@/services/MediaService";
import { isCameraPermissionPromptExpected } from "@/services/cameraPermission";
import { FacingMode } from "@/enums/FacingMode";
import { useVideoCall, videoState } from "@/composables/useVideoCall";
import { useSnapshot } from "@/composables/useSnapshot";
import type { Incident } from "@/types";
import { WebAppState } from "@/enums/WebAppState";
import { VideoState } from "@/types/VideoState";
import { isIntegratedMode } from "@/config";

const cameraStream = ref<MediaStream | null>(null);
const currentFacingMode = ref<FacingMode>(FacingMode.Environment);
const torchAvailable = ref(false);
const torchOn = ref(false);
const switchCameraInProgress = ref(false);
const toggleTorchInProgress = ref(false);

const wasActivated = ref(false);
const videoEverActivated = ref(false);
const cameraWasEverGranted = ref(false);
const videoId = ref<string | null>(null);

const oldVideoId = ref<string | null>(null);
let videoCall: ReturnType<typeof useVideoCall> | null = null;
let watchersRegistered = false;
let cameraOperationQueue = Promise.resolve(false);

export function useVideo() {
  const { sendSnapshot } = useSnapshot();
  const incidentHub = useIncidentHub();
  const mediaService = new MediaService(sendSnapshot);

  async function reportVideoState(state: WebAppState) {
    try {
      await incidentHub.setWebAppState(state);
    } catch {
      console.warn("Video state could not be reported");
    }
  }

  async function reportCameraPermissionRequest() {
    if (await isCameraPermissionPromptExpected(navigator.permissions)) {
      await reportVideoState(WebAppState.CameraPermissionRequested);
    }
  }

  async function loadVideo(incident: Incident) {
    const connection = await incidentHub.startSignalRConnection();

    connection.on("LiveVideoStarted", (liveVideo: { id: string }) => {
      videoId.value = liveVideo.id;
    });

    connection.on("LiveVideoEnded", async () => {
      await stopVideo();
    });

    connection.on("SwitchCameraRequest", () => {
      void switchFacingMode();
    });

    connection.on("ToggleTorchRequest", async () => {
      await toggleTorch();
    });

    connection.on("TakeCallerCameraSnapshot", () => {
      mediaService.takeSnapshot2();
    });

    if (incident.liveVideo) {
      wasActivated.value = true;
      videoId.value = incident.liveVideo.id;
    }
  }

  //TODO: spravne vybirani kamery na vsech zarizenich
  function queueCameraOperation(operation: () => Promise<boolean>): Promise<boolean> {
    const queuedOperation = cameraOperationQueue.then(operation);
    cameraOperationQueue = queuedOperation.then(() => false, () => false);
    return queuedOperation;
  }

  // Each caller tracks its own in-flight flag so one action's completion can't
  // re-enable a button for a different, still-queued action.
  function withProgressFlag(flag: Ref<boolean>, operation: () => Promise<boolean>): Promise<boolean> {
    flag.value = true;
    const queuedOperation = queueCameraOperation(operation);
    queuedOperation.finally(() => {
      flag.value = false;
    });
    return queuedOperation;
  }

  function switchFacingMode(): Promise<boolean> {
    return withProgressFlag(switchCameraInProgress, async () => {
      const newFacingMode =
        currentFacingMode.value === FacingMode.Environment
          ? FacingMode.User
          : FacingMode.Environment;

      currentFacingMode.value = newFacingMode;

      if (!cameraStream.value) {
        return false;
      }

      try {
        resetTorchState();
        await reportCameraPermissionRequest();

        const newCameraStream = await mediaService.switchLocalCameraStream(cameraStream.value, newFacingMode);
        cameraStream.value = newCameraStream;
        await reportVideoState(newCameraStream ? WebAppState.CameraPermissionGranted : WebAppState.CameraPermissionRejected);

        const track = newCameraStream?.getVideoTracks()[0];
        if (!track) {
          await reportTorchState();
          await reportCameraSwitchFailed();
          return false;
        }

        if (videoCall?.isActive()) {
          await videoCall.replaceVideoTrack(track);
          await reportVideoState(WebAppState.VideoTransmissionActive);
        }

        await updateTorchState(track);
        return true;
      } catch (err) {
        console.error("switchFacingMode failed", err);
        incidentHub.setWebAppState(WebAppState.VideoTransmissionFailed);
        await reportTorchState();
        await reportCameraSwitchFailed();
        return false;
      }
    });
  }

  function resetTorchState() {
    torchAvailable.value = false;
    torchOn.value = false;
  }

  async function reportTorchState() {
    if (videoCall?.isActive()) {
      try {
        await videoCall.reportTorchState(torchAvailable.value, torchOn.value);
      } catch (err) {
        console.warn("Failed to report torch state", err);
      }
    }
  }

  async function reportCameraSwitchFailed() {
    if (videoCall?.isActive()) {
      try {
        await videoCall.reportCameraSwitchFailed();
      } catch (err) {
        console.warn("Failed to report camera switch failure", err);
      }
    }
  }

  async function reportTorchToggleFailed() {
    if (videoCall?.isActive()) {
      try {
        await videoCall.reportTorchToggleFailed();
      } catch (err) {
        console.warn("Failed to report torch toggle failure", err);
      }
    }
  }

  async function updateTorchState(track: MediaStreamTrack) {
    const state = mediaService.getTorchState(track);
    torchAvailable.value = state.available;
    torchOn.value = state.on;
    await reportTorchState();
  }

  async function toggleTorch(): Promise<boolean> {
    return withProgressFlag(toggleTorchInProgress, async () => {
      const track = cameraStream.value?.getVideoTracks()[0];
      if (!track) {
        return false;
      }

      try {
        const currentState = mediaService.getTorchState(track);
        torchAvailable.value = currentState.available;
        torchOn.value = currentState.on;
        if (!currentState.available) {
          await reportTorchState();
          await reportTorchToggleFailed();
          return false;
        }

        const changed = await mediaService.setTorchState(track, !currentState.on);
        if (!changed) {
          await reportTorchState();
          await reportTorchToggleFailed();
          return false;
        }

        torchOn.value = !currentState.on;
        await reportTorchState();
        return true;
      } catch (err) {
        console.warn("toggleTorch failed", err);
        await reportTorchState();
        await reportTorchToggleFailed();
        return false;
      }
    });
  }

  if (!watchersRegistered) {
    watchersRegistered = true;

    watch(videoId, async (newVideoId) => {
      await connectToVideo(newVideoId);
    });

    watch(videoState, async (newState) => {
      console.log("Video state changed:", newState);
      if (newState === VideoState.UnsupportedBrowser) {
        incidentHub.setWebAppState(WebAppState.UnsupportedBrowser);
      }
    });
  }

  async function connectToVideo(newVideoId: string | null) {
    if (!newVideoId || newVideoId === oldVideoId.value) {
      return;
    }

    console.log("Video ID changed:", newVideoId);

    const oldId = oldVideoId.value;

    oldVideoId.value = newVideoId;

    if (oldId) {
      await stopVideo();
    }

    await startVideo(newVideoId);
  }

  function startVideo(currentVideoId: string): Promise<boolean> {
    wasActivated.value = true;
    videoEverActivated.value = true;

    return queueCameraOperation(async () => {
      try {
        if (!cameraStream.value) {
          await reportCameraPermissionRequest();
          cameraStream.value =
            await mediaService.getCameraLocalStream(currentFacingMode.value);
          await reportVideoState(cameraStream.value ? WebAppState.CameraPermissionGranted : WebAppState.CameraPermissionRejected);
        }

        const videoTrack = cameraStream.value?.getVideoTracks()[0];

        if (!videoTrack) {
          console.warn("Camera not available — video UI shown without camera stream");
          cameraStream.value = null;
          return false;
        }

        cameraWasEverGranted.value = true;

        await reportVideoState(WebAppState.StartingVideoTransmission);

        videoCall = useVideoCall(currentVideoId);
        await videoCall.startCall(videoTrack);
        await updateTorchState(videoTrack);

        await reportVideoState(WebAppState.VideoTransmissionActive);
        return true;
      } catch (err) {
        console.error("startVideo failed", err);
        incidentHub.setWebAppState(WebAppState.VideoTransmissionFailed);
        return false;
      }
    });
  }

  function startCamera(): Promise<boolean> {
    return queueCameraOperation(async () => {
      try {
        await reportCameraPermissionRequest();
        cameraStream.value =
          await mediaService.getCameraLocalStream(currentFacingMode.value);

        const track = cameraStream.value?.getVideoTracks()[0];
        if (!track) {
          console.warn("startCamera: camera access denied or unavailable");
          cameraStream.value = null;
          await reportVideoState(WebAppState.CameraPermissionRejected);
          return false;
        }

        await reportVideoState(WebAppState.CameraPermissionGranted);
        cameraWasEverGranted.value = true;

        resetTorchState();

        if (videoCall?.isActive()) {
          // Video call already exists with an active producer — replace track and resume
          await videoCall.replaceVideoTrack(track);
          await videoCall.resumeProducer();
        } else if (videoId.value) {
          await reportVideoState(WebAppState.StartingVideoTransmission);
          // No video call yet, or previous instance was cleaned up — create a new one
          videoCall = useVideoCall(videoId.value);
          await videoCall.startCall(track);
          await reportVideoState(WebAppState.VideoTransmissionActive);
        }

        await updateTorchState(track);

        return true;
      } catch (err) {
        console.error("startCamera failed", err);
        incidentHub.setWebAppState(WebAppState.VideoTransmissionFailed);
        return false;
      }
    });
  }

  function stopCamera(): Promise<boolean> {
    return queueCameraOperation(async () => {
      try {
        if (videoCall?.isActive()) {
          videoCall.setIntentionalTrackStop(true);
          await videoCall.pauseProducer();
        }

        cameraStream.value?.getTracks().forEach(t => t.stop());
        cameraStream.value = null;
        resetTorchState();
        await reportTorchState();

        if (videoCall) {
          videoCall.setIntentionalTrackStop(false);
        }

        return true;
      } catch (err) {
        console.warn("stopCamera error", err);
        return false;
      }
    });
  }

  async function pauseVideo() {
    videoCall?.pauseProducer();
  }

  async function resumeVideo() {
    videoCall?.resumeProducer();
  }

  function stopVideo(): Promise<boolean> {
    return queueCameraOperation(async () => {
      try {
        await videoCall?.endCall();
        videoCall = null;

        cameraStream.value?.getTracks().forEach(t => t.stop());
        cameraStream.value = null;
        resetTorchState();

        wasActivated.value = false;

        return true;
      } catch (err) {
        console.warn("stopVideo error", err);
        return false;
      }
    });
  }

  // window.toggleFlashlight/isFlashlightAvailable/isFlashlightOn are a fixed external contract —
  // Google's native Android ELS integration calls these exact global names
  // (Docs/External/GoogleEls/els-live-video.md) — only the assigned value is renamed internally.
  if (isIntegratedMode) {
    const getTorchAvailable = () => torchAvailable.value;
    const getTorchOn = () => torchOn.value;

    window.switchCamera = switchFacingMode;
    window.toggleFlashlight = toggleTorch;
    window.isFlashlightAvailable = getTorchAvailable;
    window.isFlashlightOn = getTorchOn;

    onBeforeUnmount(() => {
      if (window.switchCamera === switchFacingMode) window.switchCamera = undefined;
      if (window.toggleFlashlight === toggleTorch) window.toggleFlashlight = undefined;
      if (window.isFlashlightAvailable === getTorchAvailable) window.isFlashlightAvailable = undefined;
      if (window.isFlashlightOn === getTorchOn) window.isFlashlightOn = undefined;
    });
  }

  return {
    loadVideo,
    switchFacingMode,
    toggleTorch,
    stopVideo,
    startCamera,
    stopCamera,
    pauseVideo,
    resumeVideo,
    videoState,
    wasActivated,
    videoEverActivated,
    cameraWasEverGranted,
    cameraStream,
    torchAvailable,
    torchOn,
    switchCameraInProgress,
    toggleTorchInProgress,
  };
}
